// src/features/auth/Login.jsx
import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, ArrowRight, AlertCircle, ShoppingBag } from 'lucide-react'; // ← Added ShoppingBag
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { toast } from 'sonner';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { login, loading: authLoading, error: authError, isAuthenticated, clearError, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // ============================================
  // EXTRACT REDIRECT CONTEXT FROM PRODUCTCARD
  // ============================================

  const redirectMessage = location.state?.message;
  const redirectFrom = location.state?.from;

  // ============================================
  // REDIRECT IF ALREADY AUTHENTICATED
  // ============================================

  useEffect(() => {
    if (isAuthenticated) {
      const from = redirectFrom || location.state?.from?.pathname;
      if (from) {
        navigate(from, { replace: true });
      } else if (user?.role === 'admin') {
        navigate('/admin/dashboard', { replace: true });
      } else {
        navigate('/', { replace: true });
      }
    }
  }, [isAuthenticated, navigate, location, redirectFrom, user]);

  // ============================================
  // CLEAR AUTH ERROR WHEN COMPONENT UNMOUNTS
  // ============================================

  useEffect(() => {
    return () => {
      clearError?.();
    };
  }, [clearError]);

  // ============================================
  // FORM VALIDATION
  // ============================================

  const validateForm = () => {
    const normalizedEmail = email.trim().toLowerCase();

    // Email validation
    if (!normalizedEmail) {
      toast.error('Email is required');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(normalizedEmail)) {
      toast.error('Please enter a valid email address');
      return false;
    }

    // Password validation
    if (!password) {
      toast.error('Password is required');
      return false;
    }

    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return false;
    }

    return true;
  };

  // ============================================
  // HANDLE SUBMIT
  // ============================================

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Clear any previous errors
    clearError?.();

    // Validate form
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Normalize email before sending
      const normalizedEmail = email.trim().toLowerCase();

      // Call login from AuthContext
      const result = await login(normalizedEmail, password);

      // Check if login was successful
      if (!result.success) {
        const errorMessage = result.error || 'Login failed';

        // Check for specific error scenarios
        if (errorMessage.includes('locked')) {
          toast.error(
            <div>
              <p className="font-semibold">{errorMessage}</p>
              <Link
                to="/forgot-password"
                className="text-sm underline mt-1 block"
              >
                Reset your password
              </Link>
            </div>,
            { duration: 8000 }
          );
        } else if (errorMessage.includes('Too many')) {
          toast.error(errorMessage, { duration: 6000 });
        } else {
          toast.error(errorMessage);
        }

        if (result.requestId) {
          console.error('Login failed', {
            requestId: result.requestId,
            error: result.error,
          });
        }

        setPassword('');
        return;
      }
      // Check if OTP is required
      if (result.requires_otp) {
        navigate('/verify-otp', { state: location.state });
        return;
      }



      // Login successful!
      const user = result.data;

      // Show success message
      toast.success(`Welcome back, ${user.full_name || user.email}!`);

      // Navigate based on role OR redirect context
      const from = redirectFrom || location.state?.from?.pathname;

      if (user.role === 'admin') {
        navigate(from || '/admin/dashboard', { replace: true });
      } else {
        navigate(from || '/', { replace: true });
      }

      // Clear form
      setEmail('');
      setPassword('');
    } catch (err) {
      console.error('Unexpected login error:', err);
      toast.error('An unexpected error occurred. Please try again.');
      setPassword('');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ============================================
  // LOADING STATE
  // ============================================

  const isLoading = isSubmitting || authLoading;

  // ============================================
  // RENDER
  // ============================================

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img
          src="https://images.unsplash.com/photo-1594140701076-3400bf982497?w=1200&q=80"
          alt="Jewellery Collection"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/40" />
        <div className="absolute inset-0 flex items-center justify-center text-white text-center p-8">
          <div>
            <h2 className="font-playfair text-4xl mb-4">Welcome Back</h2>
            <p className="text-lg">Sign in to access your account</p>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="w-full max-w-md"
        >
          <div className="mb-8">
            <h1 className="font-playfair text-3xl mb-2">Sign In</h1>
            <p className="text-gray-600">
              Enter your credentials to access your account
            </p>
          </div>

          {/* 🛍️ PRODUCT CARD REDIRECT MESSAGE (NEW) */}
          {redirectMessage && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 bg-antique-gold/10 border-2 border-antique-gold rounded-lg"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-antique-gold rounded-full flex items-center justify-center flex-shrink-0">
                  <ShoppingBag className="w-5 h-5 text-void-black" strokeWidth={2.5} />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-manrope font-semibold text-void-black mb-1">
                    Login Required
                  </p>
                  <p className="text-sm font-manrope text-gray-700">
                    {redirectMessage}
                  </p>
                </div>
              </div>
            </motion.div>
          )}

          {/* Show global auth error if present */}
          {authError && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-red-800">{authError}</p>
                {authError.includes('locked') && (
                  <Link
                    to="/forgot-password"
                    className="text-sm text-red-600 underline mt-1 inline-block"
                  >
                    Reset your password
                  </Link>
                )}
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email Input */}
            <div>
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                disabled={isLoading}
                autoComplete="email"
                required
                className="mt-2"
              />
            </div>

            {/* Password Input */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label htmlFor="password">Password</Label>
                <Link
                  to="/forgot-password"
                  className="text-sm text-antique-gold hover:underline"
                  tabIndex={-1}
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  disabled={isLoading}
                  autoComplete="current-password"
                  required
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 disabled:opacity-50"
                  disabled={isLoading}
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <EyeOff size={18} />
                  ) : (
                    <Eye size={18} />
                  )}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={isLoading}
              className="w-full btn-gold group"
            >
              {isLoading ? (
                <>
                  <span className="animate-pulse">Signing in...</span>
                </>
              ) : (
                <>
                  Sign In
                  <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </Button>
          </form>

          {/* Register Link */}
          <div className="mt-8 text-center">
            <p className="text-gray-600">
              Don't have an account?{' '}
              <Link
                to="/register"
                className="text-antique-gold hover:underline font-medium"
              >
                Create one
              </Link>
            </p>
          </div>

          {/* Email Verification Notice */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-500">
              Need to verify your email?{' '}
              <Link
                to="/resend-verification"
                className="text-antique-gold hover:underline"
              >
                Resend verification email
              </Link>
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}